import { RefObject, useEffect, useMemo, useRef, useState } from 'react'
import throttle from 'lodash/throttle'

import { getRefDimension } from '../../utils/util'
import useMounted from '../../services/useMounted'

interface UseIsUnderSelectedElementProps {
  elementRef?: RefObject<HTMLDivElement>
  // Offset from the bottom of the element
  offset?: number | { scrollUpOffset?: number, scrollDownOffset?: number }
}

const useIsUnderSelectedElement = (props: UseIsUnderSelectedElementProps) => {
  const { elementRef, offset = 0 } = props

  const [isUnderTargetElement, setIsUnderTargetElement] = useState(false)
  const lastScrollYRef = useRef(0)
  const isMounted = useMounted()

  const scrollY = isMounted ? window.scrollY : 0
  const scrollingDown = scrollY > lastScrollYRef.current

  // Check wether offset param is a number or an object
  // and set offset value accordingly
  const isOffsetObject = typeof offset === 'object'
  const scrollUpOffset = isOffsetObject && offset.scrollUpOffset
    ? offset.scrollUpOffset
    : offset as number
  const scrollDownOffset = isOffsetObject && offset.scrollDownOffset
    ? offset.scrollDownOffset
    : offset as number

  const getTargetElementBottom = () => (isMounted ? getRefDimension('bottom', elementRef)
      + document.documentElement.scrollTop
      - (scrollingDown ? scrollDownOffset : scrollUpOffset) : 0)

  const targetElementBottom = useMemo(() => getTargetElementBottom(),
    [scrollingDown, scrollDownOffset, scrollUpOffset])

  useEffect(() => {
    const triggerIsUnderSelectedElement = throttle(() => {
      const currentScrollY = window.scrollY
      if (currentScrollY > targetElementBottom) {
        setIsUnderTargetElement(true)
      } else if (currentScrollY < targetElementBottom) {
        setIsUnderTargetElement(false)
      } else {
        setIsUnderTargetElement(true)
      }

      lastScrollYRef.current = currentScrollY
    }, 200)

    if (targetElementBottom) {
      window.addEventListener('scroll', triggerIsUnderSelectedElement, { passive: true })
    }

    return () => window.removeEventListener('scroll', triggerIsUnderSelectedElement)
  }, [targetElementBottom])

  return isUnderTargetElement
}

export default useIsUnderSelectedElement
